import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { adminAPI } from '../../utils/api';
import AssessmentReview, { calculateAssessmentAverage } from './AssessmentReview';
import { openAssessmentExport } from '../../utils/assessmentExport';

const GradingInterface = ({ readOnly = false, viewerId = null }) => {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gradeInputs, setGradeInputs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [autoGrading, setAutoGrading] = useState({});
  const [editingFeedback, setEditingFeedback] = useState({}); // {recordingId: true/false}
  const [tempFeedback, setTempFeedback] = useState({}); // {recordingId: tempText}
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, recordingId: null, questionText: '' });
  const [resettingRecordingId, setResettingRecordingId] = useState(null);

  useEffect(() => {
    loadCandidates();
  }, [readOnly, viewerId]);

  const loadCandidates = async () => {
    setLoading(true);
    setError('');

    try {
      let data;
      if (readOnly && viewerId) {
        // Viewer mode - use viewer API
        const { viewerAPI } = await import('../../utils/api');
        data = await viewerAPI.getAssignedCandidates(viewerId);
      } else {
        // Admin mode
        data = await adminAPI.getCandidatesForGrading();
      }
      setCandidates(data.candidates);
    } catch (err) {
      setError(err.message || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadCandidateRecordings = async (candidateId) => {
    setLoading(true);
    setError('');

    try {
      let data;
      if (readOnly && viewerId) {
        // Viewer mode - use viewer API
        const { viewerAPI } = await import('../../utils/api');
        data = await viewerAPI.getCandidateRecordings(candidateId, viewerId);
      } else {
        // Admin mode
        data = await adminAPI.getCandidateRecordings(candidateId);
      }
      setCandidateData(data);
      setSelectedCandidate(candidateId);

      // Initialize grade inputs with existing grades
      const inputs = {};
      data.assignments.forEach((assignment) => {
        assignment.questions.forEach((question) => {
          if (question.grade) {
            inputs[question.recording.id] = {
              score: question.grade.score_percentage,
              feedback: question.grade.feedback || '',
            };
          } else {
            inputs[question.recording.id] = {
              score: '',
              feedback: '',
            };
          }
        });
      });
      setGradeInputs(inputs);
    } catch (err) {
      setError(err.message || 'Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = (recordingId, field, value) => {
    setGradeInputs((prev) => ({
      ...prev,
      [recordingId]: {
        ...prev[recordingId],
        [field]: value,
      },
    }));
  };

  const handleSubmitGrade = async (recordingId) => {
    const gradeData = gradeInputs[recordingId];

    if (gradeData.score === '' || gradeData.score < 0 || gradeData.score > 100) {
      alert('Please enter a valid score between 0 and 100');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await adminAPI.submitGrade(
        recordingId,
        parseFloat(gradeData.score),
        gradeData.feedback
      );

      // Reload candidate data to show updated grades
      await loadCandidateRecordings(selectedCandidate);

      // Show success message
      alert('Grade submitted successfully!');
    } catch (err) {
      setError('Failed to submit grade: ' + err.message);
      alert('Failed to submit grade: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoGrade = async (recordingId) => {
    setAutoGrading((prev) => ({ ...prev, [recordingId]: true }));
    setError('');

    try {
      const result = await adminAPI.triggerAutoGrade(recordingId);

      // Update grade inputs with result
      setGradeInputs((prev) => ({
        ...prev,
        [recordingId]: {
          score: result.score_percentage,
          feedback: result.feedback || '',
        },
      }));

      // Reload candidate data to show updated grades
      await loadCandidateRecordings(selectedCandidate);

      alert(t('admin.grading.autoGradeSuccess', 'Auto-grading completed successfully!'));
    } catch (err) {
      setError(err.message || t('admin.grading.autoGradeError', 'Failed to auto-grade recording'));
      alert(t('admin.grading.autoGradeError', 'Failed to auto-grade recording') + ': ' + err.message);
    } finally {
      setAutoGrading((prev) => ({ ...prev, [recordingId]: false }));
    }
  };

  const handleOpenResetDialog = (recordingId, questionText) => {
    setResetDialog({ open: true, recordingId, questionText });
  };

  const handleCloseResetDialog = () => {
    setResetDialog({ open: false, recordingId: null, questionText: '' });
  };

  const handleResetQuestion = async () => {
    const { recordingId } = resetDialog;
    setResettingRecordingId(recordingId);

    try {
      await adminAPI.resetRecording(recordingId);
      alert('Question reset successfully. Candidate can now reanswer this question.');

      // Reload candidate data to reflect changes
      await loadCandidateRecordings(selectedCandidate);

    } catch (err) {
      setError(err.message || 'Failed to reset question');
      alert('Failed to reset question: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResettingRecordingId(null);
      handleCloseResetDialog();
    }
  };

  const handleStartEdit = (recordingId) => {
    setEditingFeedback((prev) => ({ ...prev, [recordingId]: true }));
    setTempFeedback((prev) => ({
      ...prev,
      [recordingId]: gradeInputs[recordingId]?.feedback || ''
    }));
  };

  const handleCancelEdit = (recordingId) => {
    setEditingFeedback((prev) => ({ ...prev, [recordingId]: false }));
    setTempFeedback((prev) => ({ ...prev, [recordingId]: '' }));
  };

  const handleSaveEdit = (recordingId) => {
    handleGradeChange(recordingId, 'feedback', tempFeedback[recordingId] || '');
    setEditingFeedback((prev) => ({ ...prev, [recordingId]: false }));
  };

  const handleExportGrades = async (assignment) => {
    const avg = calculateAssessmentAverage(assignment.questions);

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Grades');

    // Define styles
    const cyanFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF87CEEB' } };
    const thinBorder = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    const baseAlignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

    // Set column widths
    worksheet.columns = [
      { width: 6 },   // A - number
      { width: 45 },  // B - question text
      { width: 12 },  // C - score
      { width: 55 },  // D - feedback
    ];

    // Row 1: CANDIDATO header with email in column C
    const row1 = worksheet.addRow(['CANDIDATO', '', candidateData.candidate.email, '']);
    row1.eachCell((cell) => {
      cell.fill = cyanFill;
      cell.border = thinBorder;
      cell.alignment = baseAlignment;
      cell.font = { bold: true };
    });

    // Row 2: Column headers
    const row2 = worksheet.addRow(['', '', 'SCORE', 'FEEDBACK']);
    row2.eachCell((cell, colNumber) => {
      cell.border = thinBorder;
      cell.alignment = baseAlignment;
      if (colNumber >= 3) {
        cell.font = { bold: true };
      }
    });

    // Row 3: Assessment name with average (PREGUNTA row)
    const row3 = worksheet.addRow([assignment.assessment_name, '', avg ? `${avg}%` : '', '']);
    row3.eachCell((cell, colNumber) => {
      if (colNumber <= 2) {
        cell.fill = cyanFill;
      }
      cell.border = thinBorder;
      cell.alignment = baseAlignment;
      cell.font = { bold: colNumber === 1 };
    });
    // Merge cells A3:B3 for assessment name
    worksheet.mergeCells('A3:B3');

    // Add question rows
    assignment.questions.forEach((question, index) => {
      const row = worksheet.addRow([
        index + 1,
        question.question_text,
        question.grade?.score_percentage ? `${question.grade.score_percentage}%` : '',
        question.grade?.feedback || ''
      ]);

      row.eachCell((cell, colNumber) => {
        cell.border = thinBorder;
        cell.alignment = baseAlignment;
        // Smaller font for feedback column (column 4)
        if (colNumber === 4) {
          cell.font = { size: 10 };
        }
      });
    });

    // Auto-fit row heights for wrapped text
    worksheet.eachRow((row) => {
      row.height = undefined; // Let Excel auto-calculate
    });

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const safeEmail = candidateData.candidate.email.replace(/[^a-zA-Z0-9]/g, '_');
    const safeAssessment = assignment.assessment_name.replace(/[^a-zA-Z0-9]/g, '_');
    saveAs(new Blob([buffer]), `grades_${safeEmail}_${safeAssessment}.xlsx`);
  };

  const handleExportPDF = async (assignment) => {
    setIsExportingPDF(true);
    try {
      await openAssessmentExport({
        candidate: candidateData.candidate,
        assignment,
        limitedAccess: readOnly && !candidateData.has_full_access,
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      alert(`Failed to export PDF: ${error.message}`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (loading && !candidateData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Grading Interface
      </Typography>

      {readOnly && candidateData && !candidateData.has_full_access && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>⚠️ Trial Access:</strong> You are viewing limited results (first question only).
          Contact the platform administrator to request full access to view all questions, recordings, and complete grading details.
        </Alert>
      )}

      {readOnly && candidateData && candidateData.has_full_access && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Read-Only Mode:</strong> You have full access to view all grades and recordings but cannot submit or modify grades.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedCandidate ? (
        // Candidate selection view
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Select Candidate to Grade
          </Typography>

          {candidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No candidates with completed tests.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell align="center"><strong>Completed Tests</strong></TableCell>
                    <TableCell align="center"><strong>Pending Grades</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((candidate) => (
                    <TableRow key={candidate.id}>
                      <TableCell>{candidate.email}</TableCell>
                      <TableCell align="center">{candidate.completed_assignments}</TableCell>
                      <TableCell align="center">{candidate.pending_grades}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => loadCandidateRecordings(candidate.id)}
                        >
                          Grade
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        // Recording review view
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Grading: {candidateData.candidate.email}
            </Typography>
            <Button variant="outlined" onClick={() => setSelectedCandidate(null)}>
              Back to Candidates
            </Button>
          </Box>

          {candidateData.assignments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No recordings available.
            </Typography>
          ) : (
            candidateData.assignments.map((assignment) => (
              <AssessmentReview
                key={assignment.assignment_id}
                assignment={assignment}
                readOnly={readOnly}
                limitedAccess={readOnly && !candidateData.has_full_access}
                onExportGrades={handleExportGrades}
                onExportPDF={handleExportPDF}
                isExportingPDF={isExportingPDF}
                grading={{
                  gradeInputs, editingFeedback, tempFeedback, setTempFeedback,
                  submitting, autoGrading, resettingRecordingId, handleGradeChange,
                  handleOpenResetDialog, handleStartEdit, handleSaveEdit, handleCancelEdit,
                  handleSubmitGrade, handleAutoGrade,
                }}
              />
            ))
          )}
        </>
      )}

      {/* Reset Question Confirmation Dialog */}
      <Dialog
        open={resetDialog.open}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Question for Candidate?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Question:</strong> {resetDialog.questionText}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This will permanently delete:
          </DialogContentText>
          <DialogContentText component="ul" sx={{ pl: 2 }}>
            <li>The candidate's video recording</li>
            <li>All associated grades and feedback</li>
            <li>Any comments</li>
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            The candidate will be able to reanswer this question from scratch.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
            ⚠️ This action CANNOT be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Cancel</Button>
          <Button
            onClick={handleResetQuestion}
            color="error"
            variant="contained"
            disabled={resettingRecordingId !== null}
          >
            Reset Question
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GradingInterface;
